<template>
<div class="promotion-container backpack-promotion">
    <div class="promotion">
        <div class="wordmark">
            <NuxtImg :src="backpackCopy.logoSrc" />
        </div>
        <div class="description">
            <div v-if="!backpackActivated" class="header">
                {{ $t('SHOPPING_CART.PROMOTIONS.BACKPACK.HEADER_INACTIVE') }}
            </div>
            <div v-else class="header">
                {{ $t('SHOPPING_CART.PROMOTIONS.BACKPACK.HEADER_ACTIVE') }}
            </div>
            <div v-if="!backpackActivated" class="promotion-inactive">
                <div>{{ $t('SHOPPING_CART.PROMOTIONS.BACKPACK.DESCRIPTION_INACTIVE') }}</div>
            </div>
            <div v-else class="promotion-active">
                <div>{{ $t('SHOPPING_CART.PROMOTIONS.BACKPACK.DESCRIPTION_ACTIVE') }}</div>
            </div>
        </div>
        <div class="toggle">
            <label class="switch">
                <input
                    id="backpack-promotion-active" type="checkbox"
                    :disabled="toggleDisabled"
                    :checked="backpackActivated"
                    v-on:change="$emit('toggle')"
                >
                <span class="slider" />
            </label>
        </div>
    </div>

    <div class="bundle-info" :style="backpackBannerStyle">
        <div v-if="!backpackActivated" class="promotion-inactive">
            <div class="wordmark">
                <NuxtImg :src="backpackCopy.logoSmallSrcInactive" />
            </div>

            <div class="price">
                <div class="normal">
                    {{ $t('SHOPPING_CART.PROMOTIONS.NORMAL_PRICE_LABEL') }}
                    <span class="strikethrough">{{ backpackNormalPrice }}</span>
                </div>

                <div class="bundled">
                    {{ $t('SHOPPING_CART.PROMOTIONS.BUNDLED_PRICE_LABEL') }}
                    {{ backpackBundledPrice }}
                </div>
            </div>
        </div>

        <div v-else class="promotion-active">
            <div class="title">
                <div>{{ $t('SHOPPING_CART.PROMOTIONS.BACKPACK.INCLUDES_TITLE_LINE_1') }}</div>
                <div>{{ $t('SHOPPING_CART.PROMOTIONS.BACKPACK.INCLUDES_TITLE_LINE_2') }}</div>
            </div>

            <div class="wordmark-container">
                <div class="wordmarks">
                    <NuxtImg :src="backpackCopy.logoSmallSrcActive" />
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script setup lang="ts">
import { type ComputedRef } from 'vue'
import Shopify from '@jackboxgames/shopify'
import { formatPrice } from '$services/storefronts/storefronts'

interface BackpackCopy {
    logoSrc: string
    logoSmallSrcActive: string
    logoSmallSrcInactive: string
    bannerSrc: string
}

const BACKPACK_COPY: BackpackCopy = {
    logoSrc: '/images/promotions/backpackbundle/backpack-bundle-logo.png',
    logoSmallSrcActive: '/images/promotions/backpackbundle/backpack-bundle-list-1.png',
    logoSmallSrcInactive: '/images/promotions/backpackbundle/backpack-bundle-list-2.png',
    bannerSrc: '/images/promotions/backpackbundle/backpack-bundle-bg.png'
}

const props = defineProps({
    backpackActivated: { type: Boolean, required: true },
    toggleDisabled: { type: Boolean, required: true },
    backpackBundle: { type: Object as () => Shopify.Shopify.Product | null, default: null },
    backpackNormalPriceNumeric: { type: Number, required: true },
    backpackPriceNumeric: { type: Number, required: true }
})

defineEmits(['toggle'])

const app = useNuxtApp()

const backpackCopy = BACKPACK_COPY

const backpackBannerStyle: ComputedRef<Record<string, string>> = computed(() => {
    if (!backpackCopy.bannerSrc) { return {} }

    return {
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('${backpackCopy.bannerSrc}')`
    }
})

const backpackNormalPrice: ComputedRef<string> = computed(() => {
    if (!props.backpackBundle) { return '' }

    const currencyCode = props.backpackBundle.variants[0].price.currencyCode

    return formatPrice(
        `${props.backpackNormalPriceNumeric}`,
        currencyCode,
        app.$i18n.locale
    )
})

const backpackBundledPrice: ComputedRef<string> = computed(() => {
    if (!props.backpackBundle) { return '' }

    const currencyCode = props.backpackBundle.variants[0].price.currencyCode

    return formatPrice(
        `${props.backpackPriceNumeric}`,
        currencyCode,
        app.$i18n.locale
    )
})
</script>

<style scoped lang="scss">
@use "$styles/kit.scss" as *;

.promotion-container {
    margin: 16px -17px 0 -24px;
    border-top: 1px solid #515E71;
    background: var(--Surface-50, #3A3C43);
}

.promotion {
    display: flex;
    flex-direction: row;
    padding: 0 17px 0 24px;

    @media only screen and (max-width: $mq-cutoff-xsmall) {
        & {
            padding: 0 8px;
        }
    }

   .wordmark {
        width: 94px;
        img {
            width: 83px;
        }
    }

    .description {
        flex: 1;
        padding: 12px;
        display: flex;
        flex-direction: column;
        justify-content: center;

        @media only screen and (max-width: $mq-cutoff-xsmall) {
            & {
                padding: 8px;
                font-size: 14px;
            }
        }

        .header {
            font-weight: 600;
            margin-bottom: 12px;
        }
    }

    .toggle {
        width: 120px;
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-top: 22px;

        @media only screen and (max-width: $mq-cutoff-xsmall) {
            & {
                width: 62px;
            }
        }

        /* The switch - the box around the slider */
        .switch {
            position: relative;
            display: inline-block;
            width: 55px;
            height: 22px;

            input {
                opacity: 0;
                width: 0;
                height: 0;

                &:checked + .slider {
                    background-color: #D86500;
                }

                &:focus + .slider {
                    box-shadow: 0 0 1px #D86500;
                }

                &:disabled + .slider {
                    background-color: var(--neutral-600);
                    cursor: not-allowed;
                }

                &:checked + .slider:before {
                    transform: translateX(23px);
                    background-color: #FFBA1A;
                    border: 3px solid #D86500;
                }

                &:disabled + .slider:before {
                    cursor: not-allowed;
                    transform: translateX(23px);
                    background-color: var(--neutral-400);
                    border: 3px solid var(--neutral-500);
                }
            }
        }

        /* The slider */
        .slider {
            position: absolute;
            cursor: pointer;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            border-radius: 16px;
            background-color: var(--neutral-400);
            transition: .25s;

            &:before {
                position: absolute;
                content: "";
                height: 32px;
                width: 32px;
                left: 0;
                bottom: -5px;
                border-radius: 50%;
                background-color: var(--neutral-200);
                border: 3px solid var(--neutral-300);
                transition: .25s;
                filter: drop-shadow(0 4px 6.667px rgba(0, 0, 0, 0.02))
                    drop-shadow(0 0 2.667px rgba(0, 0, 0, 0.05))
                    drop-shadow(0 1.333px 5.333px rgba(0, 0, 0, 0.08));
            }
        }
    }
}

.bundle-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 17px 0 24px;
    height: 54px;
    border-bottom: 1px solid #515E71;
    background-size: cover;

    .promotion-active {
        display: flex;
        flex-direction: row;
        height: 100%;
        gap: 16px;

        .title {
            display: flex;
            flex-direction: column;
            justify-content: center;
            height: 100%;
            font-size: 16px;
            line-height: 16px;
            font-weight: 600;
            gap: 4px;
        }

        .wordmark-container {
            display: flex;
            flex-direction: column;
            justify-content: center;
            height: 100%;

            .wordmarks {
                display: flex;
                flex-direction: row;
                justify-content: center;
                gap: 4px;

                img {
                    height: 40px;
                }
            }
        }
    }

    .promotion-inactive {
        height: 100%;
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 20px;

        .wordmark {
            display: flex;
            flex-direction: column;
            justify-content: center;
            width: 100px;

            img {
                width: 100%;
            }
        }

        .price {
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            gap: 6px;

            .normal {
                font-size: 14px;
                font-weight: 500;
                line-height: 14px;

                .strikethrough {
                    text-decoration: line-through;
                }
            }

            .bundled {
                font-size: 16px;
                font-weight: 600;
                line-height: 16px;
            }
        }
    }
}

.backpack-promotion {
    .promotion .description .header {
        margin-bottom: 8px;
    }

    .promotion .wordmark {
        width: 130px;
        display: flex;
        align-items: center;
        justify-content: center;

        img {
            width: 116px;
        }
    }

    .bundle-info {
        .promotion-inactive .wordmark {
            width: 140px;
        }

        .promotion-active .wordmark-container .wordmarks img {
            height: 45px;
        }
    }
}
</style>
